import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Card, Spinner } from 'react-bootstrap';
import { FaPlus, FaTrophy, FaChartLine, FaBriefcase, FaMoneyBillWave, FaMedal, FaStar, FaAward } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { addCareerMilestone } from '../../services/firebase/employeeService';
import DepartmentSelector from '../common/DepartmentSelector';

const MILESTONE_TYPES = [
  { value: 'promotion_and_increment', label: 'Promotion + Salary Increment (Combined)', icon: <FaChartLine /> },
  { value: 'promotion', label: 'Promotion / Designation Change', icon: <FaBriefcase /> },
  { value: 'salary_increment', label: 'Salary Revision / Increment', icon: <FaMoneyBillWave /> },
  { value: 'role_change', label: 'Role / Department / Priority Shift', icon: <FaBriefcase /> },
  { value: 'achievement', label: 'Achievement / Special Recognition', icon: <FaTrophy /> },
  { value: 'award', label: 'Award / Honor', icon: <FaMedal /> },
  { value: 'appraisal', label: 'Performance Appraisal Review', icon: <FaStar /> },
];

const BADGE_PRESETS = [
  'Star of the Month',
  'Quarterly Top Performer',
  'Best Innovator',
  'Outstanding Leadership',
  'Client Champion',
  'Excellence in Execution',
  'Speed & Quality Master',
  'Rising Star',
  'Custom Badge'
];

const AddCareerMilestoneModal = ({ show, onHide, employee, currentUser, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: 'promotion_and_increment',
    title: '',
    effectiveDate: new Date().toISOString().split('T')[0],
    
    // Role / Promotion
    previousDesignation: '',
    newDesignation: '',
    previousDepartment: '',
    newDepartment: '',
    previousRole: '',
    newRole: '',
    
    // Salary
    previousSalary: 0,
    newSalary: 0,
    incrementAmount: 0,
    incrementPercentage: 0,
    
    // Achievement
    achievementCategory: 'Award',
    badgeName: 'Star of the Month',
    customBadgeName: '',
    
    // Description
    description: '',
    syncProfile: true
  });

  // Pre-fill existing employee values
  useEffect(() => {
    if (employee && show) {
      const currentSalary = parseFloat(employee.salary?.basicSalary) || 0;
      const currentDept = employee.job?.department || employee.department || '';
      const currentDesig = employee.job?.designation || employee.designation || '';
      const currentRole = employee.job?.role || employee.role || 'employee';

      setFormData(prev => ({
        ...prev,
        previousSalary: currentSalary,
        newSalary: currentSalary,
        incrementAmount: 0,
        incrementPercentage: 0,
        previousDepartment: currentDept,
        newDepartment: currentDept,
        previousDesignation: currentDesig,
        newDesignation: currentDesig,
        previousRole: currentRole,
        newRole: currentRole,
        title: `Promotion to ${currentDesig || 'Senior Role'} & Salary Revision`,
        syncProfile: true
      }));
    }
  }, [employee, show]);

  // Recalculate hike on salary change
  const handleSalaryChange = (newVal) => {
    const newSal = parseFloat(newVal) || 0;
    const prevSal = parseFloat(formData.previousSalary) || 0;
    const diff = newSal - prevSal;
    const pct = prevSal > 0 ? ((diff / prevSal) * 100) : 0;

    setFormData(prev => ({
      ...prev,
      newSalary: newSal,
      incrementAmount: diff,
      incrementPercentage: parseFloat(pct.toFixed(2))
    }));
  };

  const handleTypeChange = (newType) => {
    let defaultTitle = 'Career Milestone';
    if (newType === 'promotion_and_increment') defaultTitle = `Promotion to ${formData.newDesignation || 'Senior Role'} & Salary Revision`;
    else if (newType === 'promotion') defaultTitle = `Promotion to ${formData.newDesignation || 'Next Level'}`;
    else if (newType === 'salary_increment') defaultTitle = `Annual Salary Revision (${new Date().getFullYear()})`;
    else if (newType === 'achievement') defaultTitle = `Recognized for Outstanding Project Success`;
    else if (newType === 'award') defaultTitle = `Awarded: ${formData.badgeName || 'Star of the Month'}`;
    else if (newType === 'role_change') defaultTitle = `Transferred to ${formData.newDepartment || 'New Role'}`;
    else if (newType === 'appraisal') defaultTitle = `Performance Appraisal (${new Date().getFullYear()})`;

    setFormData(prev => ({
      ...prev,
      type: newType,
      title: defaultTitle
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Please enter a milestone title');
      return;
    }

    setLoading(true);
    try {
      const finalBadge = formData.badgeName === 'Custom Badge' ? formData.customBadgeName : formData.badgeName;
      
      const payload = {
        ...formData,
        badgeName: finalBadge,
        previousCTC: (formData.previousSalary || 0) * 12,
        newCTC: (formData.newSalary || 0) * 12,
      };

      await addCareerMilestone(employee.id, payload, currentUser, formData.syncProfile);
      
      toast.success('Career milestone and growth record added successfully');
      if (onSuccess) onSuccess();
      onHide();
    } catch (err) {
      console.error('Error saving milestone:', err);
      toast.error(err.message || 'Failed to record career milestone');
    } finally {
      setLoading(false);
    }
  };

  const isSalaryType = ['salary_increment', 'promotion_and_increment', 'appraisal'].includes(formData.type);
  const isPromotionType = ['promotion', 'promotion_and_increment', 'role_change'].includes(formData.type);
  const isAchievementType = ['achievement', 'award'].includes(formData.type);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton style={{ background: '#1e293b', color: '#ffffff' }}>
        <Modal.Title className="d-flex align-items-center gap-2 fs-5">
          <FaTrophy className="text-warning" />
          <span>Record Employee Career Growth & Milestone</span>
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="p-4 bg-light">
          {/* Milestone Type Picker */}
          <Card className="border-0 shadow-sm mb-3">
            <Card.Body className="p-3">
              <Form.Label className="fw-bold text-dark mb-2">Milestone / Event Type <span className="text-danger">*</span></Form.Label>
              <Form.Select 
                value={formData.type} 
                onChange={(e) => handleTypeChange(e.target.value)}
                className="fw-bold border-primary"
              >
                {MILESTONE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Form.Select>
            </Card.Body>
          </Card>

          {/* Title & Effective Date */}
          <Row className="mb-3 g-3">
            <Col md={8}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark">Milestone Title / Heading <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                  placeholder="e.g. Promoted to Senior Full Stack Developer"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark">Effective Date <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={formData.effectiveDate}
                  onChange={(e) => setFormData(p => ({ ...p, effectiveDate: e.target.value }))}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Promotion / Designation Fields */}
          {isPromotionType && (
            <Card className="border-0 shadow-sm mb-3" style={{ borderLeft: '4px solid #2563eb' }}>
              <Card.Body className="p-3">
                <h6 className="fw-bold text-primary mb-3 d-flex align-items-center gap-2">
                  <FaBriefcase /> Designation, Department & Role Elevation
                </h6>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Previous Designation</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={formData.previousDesignation}
                      onChange={(e) => setFormData(p => ({ ...p, previousDesignation: e.target.value }))}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small fw-bold text-primary mb-1">New Designation *</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={formData.newDesignation}
                      onChange={(e) => setFormData(p => ({ ...p, newDesignation: e.target.value }))}
                      placeholder="e.g. Lead Engineer / Specialist"
                      required={isPromotionType}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Previous Department</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={formData.previousDepartment}
                      onChange={(e) => setFormData(p => ({ ...p, previousDepartment: e.target.value }))}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small fw-bold text-primary mb-1">New Department</Form.Label>
                    <DepartmentSelector
                      value={formData.newDepartment}
                      onChange={(val) => setFormData(p => ({ ...p, newDepartment: val }))}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Previous System Role</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={formData.previousRole} 
                      disabled
                      className="bg-light text-capitalize"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small fw-bold text-primary mb-1">New System Role</Form.Label>
                    <Form.Select
                      value={formData.newRole}
                      onChange={(e) => setFormData(p => ({ ...p, newRole: e.target.value }))}
                    >
                      <option value="employee">Employee</option>
                      <option value="dept_head">Department Head</option>
                      <option value="production_head">Production Head</option>
                      <option value="manager">Manager</option>
                      <option value="hr">HR</option>
                      <option value="director">Director</option>
                      <option value="admin">Admin</option>
                    </Form.Select>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Salary Revision Fields */}
          {isSalaryType && (
            <Card className="border-0 shadow-sm mb-3" style={{ borderLeft: '4px solid #059669' }}>
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold text-success mb-0 d-flex align-items-center gap-2">
                    <FaMoneyBillWave /> Salary Revision & Hike Calculation
                  </h6>
                  {formData.incrementAmount > 0 && (
                    <span style={{ background: '#d1fae5', color: '#047857', border: '1px solid #a7f3d0', fontSize: '0.8rem', fontWeight: 700, padding: '3px 10px', borderRadius: '6px' }}>
                      +{formData.incrementPercentage}% Hike (+₹{formData.incrementAmount.toLocaleString('en-IN')})
                    </span>
                  )}
                </div>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Previous Basic Salary (₹/month)</Form.Label>
                    <Form.Control 
                      type="number" 
                      value={formData.previousSalary}
                      onChange={(e) => setFormData(p => ({ ...p, previousSalary: parseFloat(e.target.value) || 0 }))}
                      min="0"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small fw-bold text-success mb-1">New Revised Basic Salary (₹/month) *</Form.Label>
                    <Form.Control 
                      type="number" 
                      value={formData.newSalary}
                      onChange={(e) => handleSalaryChange(e.target.value)}
                      min="0"
                      required={isSalaryType}
                    />
                  </Col>
                  <Col md={6}>
                    <div className="p-2 bg-white rounded border">
                      <small className="text-muted d-block">Total Increment Amount</small>
                      <span className="fw-bold text-dark fs-6">₹{formData.incrementAmount.toLocaleString('en-IN')} / month</span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-2 bg-white rounded border">
                      <small className="text-muted d-block">Estimated Annual CTC</small>
                      <span className="fw-bold text-primary fs-6">₹{((formData.newSalary || 0) * 12).toLocaleString('en-IN')} / annum</span>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Achievement & Recognition Fields */}
          {isAchievementType && (
            <Card className="border-0 shadow-sm mb-3" style={{ borderLeft: '4px solid #d97706' }}>
              <Card.Body className="p-3">
                <h6 className="fw-bold text-warning mb-3 d-flex align-items-center gap-2">
                  <FaTrophy /> Recognition & Badge Details
                </h6>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label className="small fw-semibold">Recognition Category</Form.Label>
                    <Form.Select 
                      value={formData.achievementCategory} 
                      onChange={(e) => setFormData(p => ({ ...p, achievementCategory: e.target.value }))}
                    >
                      <option value="Award">Company Award</option>
                      <option value="Project Success">Major Project Delivery</option>
                      <option value="Certification">Professional Certification</option>
                      <option value="Special Recognition">Special Contribution / Recognition</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small fw-semibold">Badge / Award Name</Form.Label>
                    <Form.Select 
                      value={formData.badgeName} 
                      onChange={(e) => setFormData(p => ({ ...p, badgeName: e.target.value }))}
                    >
                      {BADGE_PRESETS.map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  {formData.badgeName === 'Custom Badge' && (
                    <Col md={12}>
                      <Form.Label className="small fw-semibold">Enter Custom Badge Name</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={formData.customBadgeName}
                        onChange={(e) => setFormData(p => ({ ...p, customBadgeName: e.target.value }))}
                        placeholder="e.g. Master Communicator"
                        required
                      />
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Description / Notes */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold text-dark">Description / Appraisal Remarks / Justification</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
              placeholder="Provide context regarding this promotion, performance justification, key achievements delivered, or reasons for the appraisal..."
            />
          </Form.Group>

          {/* Profile Auto-Sync Checkbox */}
          {(isPromotionType || isSalaryType) && (
            <Card className="border-0 bg-white p-3 shadow-sm">
              <Form.Check
                type="checkbox"
                id="syncProfileCheckbox"
                label={
                  <span>
                    <strong>Auto-sync with active employee profile:</strong> Automatically update current designation, department, role, and base salary in the live employee profile.
                  </span>
                }
                checked={formData.syncProfile}
                onChange={(e) => setFormData(p => ({ ...p, syncProfile: e.target.checked }))}
              />
            </Card>
          )}
        </Modal.Body>

        <Modal.Footer className="bg-white">
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading} className="px-4 fw-bold">
            {loading ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Recording Milestone...
              </>
            ) : (
              'Save & Record Milestone'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddCareerMilestoneModal;
